import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import * as Parse from 'parse';
import * as Sentry from '@sentry/react';
import {AppThunk} from 'state';
import {formatTable} from 'helpers/tableFormatter';
import {TableClassName, TableItem} from './types';
import {uniqBy} from 'lodash';
const TABLES_PAGE_SIZE = 20;
interface TablesItemsState {
  items: TableItem[];
  isLoading: boolean;
}
interface State {
  error: string | null;
  addLoading: boolean;
  updateLoading: boolean;
  deleteLoading: boolean;
  availableTables: TablesItemsState;
  unavailableTables: TablesItemsState & {
    isInitialLoadingDone: boolean;
    totalCount: number;
  };
  allUnavailableTables: TablesItemsState;
  addSuccessNotification: boolean;
  updateSuccessNotification: boolean;
  deleteSuccessNotification: boolean;
}

const initialState: State = {
  error: null,
  addLoading: false,
  updateLoading: false,
  deleteLoading: false,
  availableTables: {
    items: [],
    isLoading: false,
  },
  unavailableTables: {
    items: [],
    isLoading: false,
    isInitialLoadingDone: false,
    totalCount: 0,
  },
  allUnavailableTables: {
    items: [],
    isLoading: false,
  },
  addSuccessNotification: false,
  updateSuccessNotification: false,
  deleteSuccessNotification: false,
};

const tablesSlice = createSlice({
  name: 'tables',
  initialState,
  reducers: {
    fetchAvailableTablesStart(state) {
      state.availableTables.isLoading = true;
      state.error = null;
    },
    fetchAvailableTablesSuccess(state, action: PayloadAction<TableItem[]>) {
      state.availableTables.isLoading = false;
      state.availableTables.items = action.payload;
    },
    fetchAvailableTablesFailed(state, action: PayloadAction<string>) {
      state.availableTables.isLoading = false;
      state.error = action.payload;
    },
    fetchUnavailableTablesStart(state) {
      state.unavailableTables.isLoading = true;
      state.error = null;
    },
    fetchUnavailableTablesSuccess(
      state,
      action: PayloadAction<{
        items: TableItem[];
        isInitialLoading: boolean;
        totalCount: number;
      }>,
    ) {
      state.unavailableTables.isLoading = false;
      if (action.payload.isInitialLoading) {
        state.unavailableTables.isInitialLoadingDone = true;
      }

      state.unavailableTables.items = uniqBy(
        [
          // We only want to keep the previous state if it's not the first fetch
          ...(!action.payload.isInitialLoading
            ? state.unavailableTables.items
            : []),
          ...action.payload.items,
        ],
        'id',
      );

      state.unavailableTables.totalCount = action.payload.totalCount;
    },
    fetchUnavailableTablesFailed(state, action: PayloadAction<string>) {
      state.unavailableTables.isLoading = false;
      state.error = action.payload;
    },
    fetchAllUnavailableTablesStart(state) {
      state.allUnavailableTables.isLoading = true;
      state.error = null;
    },
    fetchAllUnavailableTablesSuccess(
      state,
      action: PayloadAction<TableItem[]>,
    ) {
      state.allUnavailableTables.isLoading = false;
      state.allUnavailableTables.items = action.payload;
    },
    fetchAllUnavailableTablesFailed(state, action: PayloadAction<string>) {
      state.allUnavailableTables.isLoading = false;
      state.error = action.payload;
    },
    addTableStart(state) {
      state.addLoading = true;
      state.addSuccessNotification = false;
      state.error = null;
    },
    addTableSuccess(state, action: PayloadAction<TableItem>) {
      state.addLoading = false;
      state.addSuccessNotification = true;
      state.availableTables.items.push(action.payload);
    },
    addTableFailed(state, action: PayloadAction<string>) {
      state.addLoading = false;
      state.error = action.payload;
    },
    updateTableStart(state) {
      state.updateLoading = true;
      state.updateSuccessNotification = false;
      state.error = null;
    },
    updateTableSuccess(state, action: PayloadAction<TableItem>) {
      state.updateLoading = false;
      state.updateSuccessNotification = true;
      for (const tableItems of [
        state.availableTables.items,
        state.unavailableTables.items,
      ]) {
        const currentTablesIndex = tableItems.findIndex(
          (table) => table.id === action.payload.id,
        );
        if (currentTablesIndex > -1) {
          tableItems[currentTablesIndex] = action.payload;
          return;
        }
      }
    },
    updateTableFailed(state, action: PayloadAction<string>) {
      state.updateLoading = false;
      state.error = action.payload;
    },
    updateTableAvailabilityStart(state) {
      state.updateLoading = true;
      state.updateSuccessNotification = false;
      state.error = null;
    },
    updateTableAvailabilitySuccess(
      state,
      action: PayloadAction<{
        table: TableItem;
        available: boolean;
      }>,
    ) {
      state.updateLoading = false;
      state.updateSuccessNotification = true;
      if (action.payload.available) {
        state.unavailableTables.items = state.unavailableTables.items.filter(
          (table) => table.id !== action.payload.table.id,
        );
        state.availableTables.items.push(action.payload.table);
      } else {
        state.availableTables.items = state.availableTables.items.filter(
          (table) => table.id !== action.payload.table.id,
        );
        state.unavailableTables.items.push(action.payload.table);
      }
    },
    updateTableAvailabilityFailed(state, action: PayloadAction<string>) {
      state.updateLoading = false;
      state.error = action.payload;
    },
    deleteTableStart(state) {
      state.deleteLoading = true;
      state.deleteSuccessNotification = false;
      state.error = null;
    },
    deleteTableSuccess(state, action: PayloadAction<string>) {
      state.deleteLoading = false;
      state.deleteSuccessNotification = true;

      for (const tableItems of [
        state.availableTables.items,
        state.unavailableTables.items,
      ]) {
        const currentTablesIndex = tableItems.findIndex(
          (table) => table.id === action.payload,
        );
        if (currentTablesIndex > -1) {
          tableItems.splice(currentTablesIndex, 1);
          return;
        }
      }
    },
    deleteTableFailed(state, action: PayloadAction<string>) {
      state.deleteLoading = false;
      state.error = action.payload;
    },
    reset(state) {
      state.updateLoading = false;
      state.addLoading = false;
      state.error = null;
      state.addSuccessNotification = false;
      state.updateSuccessNotification = false;
      state.deleteSuccessNotification = false;
      state.availableTables.isLoading = false;
      state.unavailableTables.isLoading = false;
      state.unavailableTables.isInitialLoadingDone = false;
      state.unavailableTables.totalCount = 0;
      state.allUnavailableTables.isLoading = false;
    },
    dismissNotifications(state) {
      state.addSuccessNotification = false;
      state.updateSuccessNotification = false;
      state.deleteSuccessNotification = false;
    },
    purge() {
      return initialState;
    },
  },
});

export const {
  fetchAvailableTablesStart,
  fetchAvailableTablesSuccess,
  fetchAvailableTablesFailed,
  fetchUnavailableTablesStart,
  fetchUnavailableTablesSuccess,
  fetchUnavailableTablesFailed,
  fetchAllUnavailableTablesStart,
  fetchAllUnavailableTablesSuccess,
  fetchAllUnavailableTablesFailed,
  addTableStart,
  addTableSuccess,
  addTableFailed,
  updateTableStart,
  updateTableSuccess,
  updateTableFailed,
  updateTableAvailabilityStart,
  updateTableAvailabilitySuccess,
  updateTableAvailabilityFailed,
  deleteTableStart,
  deleteTableSuccess,
  deleteTableFailed,
  reset,
  dismissNotifications,
  purge,
} = tablesSlice.actions;

export default tablesSlice.reducer;

/**
 * Thunks
 */

export const fetchUnavailableTables =
  ({isInitialLoading}: {isInitialLoading: boolean}): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchUnavailableTablesStart());

      const currentUser = Parse.User.current()!;
      const restaurant = currentUser.get('restaurant');
      const tablesQuery = new Parse.Query('Table');
      tablesQuery.equalTo('restaurant', restaurant);
      tablesQuery.equalTo('available', false);
      const limit = TABLES_PAGE_SIZE;
      const skip = isInitialLoading
        ? 0
        : getState().tables.unavailableTables.items.length;
      tablesQuery.descending('updatedAt');
      const [tables, totalCount] = await Promise.all([
        tablesQuery.skip(skip).limit(limit).find(),
        tablesQuery.count(),
      ]);

      dispatch(
        fetchUnavailableTablesSuccess({
          items: tables.map(formatTable),
          isInitialLoading,
          totalCount,
        }),
      );
    } catch (error) {
      dispatch(fetchUnavailableTablesFailed(error.toString()));
      Sentry.captureException(error);
      // eslint-disable-next-line no-console
      console.error('fetchTables error', error);
    }
  };
export const fetchAllUnavailableTables = (): AppThunk => async (dispatch) => {
  try {
    dispatch(fetchAllUnavailableTablesStart());

    const currentUser = Parse.User.current()!;
    const restaurant = currentUser.get('restaurant');
    const tablesQuery = new Parse.Query('Table');
    tablesQuery.equalTo('restaurant', restaurant);
    tablesQuery.equalTo('available', false);
    tablesQuery.descending('updatedAt');
    const tables = await tablesQuery.find();

    dispatch(fetchAllUnavailableTablesSuccess(tables.map(formatTable)));
  } catch (error) {
    dispatch(fetchAllUnavailableTablesFailed(error.toString()));
    Sentry.captureException(error);
    // eslint-disable-next-line no-console
    console.error('fetchTables error', error);
  }
};
export const fetchAdditionalUnavailableTableByTableNumber =
  (tableNumber: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchUnavailableTablesStart());

      const query = new Parse.Query(TableClassName);
      const restaurant = Parse.User.current()!.get('restaurant');
      query.equalTo('restaurant', restaurant);
      query.equalTo('available', false);
      query.equalTo('name', tableNumber);
      const tables = await query.find();

      dispatch(
        fetchUnavailableTablesSuccess({
          items: tables.map(formatTable),
          isInitialLoading: false,
          totalCount: getState().tables.unavailableTables.totalCount,
        }),
      );
    } catch (error) {
      dispatch(fetchUnavailableTablesFailed(error.toString()));
      Sentry.captureException(error);
      // eslint-disable-next-line no-console
      console.error('fetchTables error', error);
    }
  };

export const fetchAvailableTables = (): AppThunk => async (dispatch) => {
  try {
    dispatch(fetchAvailableTablesStart());

    const currentUser = Parse.User.current()!;
    const restaurant = currentUser.get('restaurant');

    const tablesQuery = new Parse.Query('Table');
    tablesQuery.equalTo('restaurant', restaurant);
    tablesQuery.equalTo('available', true);

    const tables = await tablesQuery.find();

    dispatch(fetchAvailableTablesSuccess(tables.map(formatTable)));
  } catch (error) {
    dispatch(fetchAvailableTablesFailed(error.toString()));
    Sentry.captureException(error);
    // eslint-disable-next-line no-console
    console.error('fetchTables error', error);
  }
};

interface AddUpdateTablePayload {
  name: string;
  capacity: number;
}

export const addTable =
  (formData: AddUpdateTablePayload): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(addTableStart());

      const currentUser = Parse.User.current()!;
      const restaurant = await currentUser.get('restaurant').fetch();

      const table = await new TableClassName().save({
        restaurant,
        name: formData.name,
        capacity: formData.capacity,
        available: true,
      });

      dispatch(addTableSuccess(formatTable(table)));
    } catch (error) {
      dispatch(addTableFailed(error.toString()));
      Sentry.captureException(error);
      // eslint-disable-next-line no-console
      console.error('addTable error', error);
    }
  };

export const updateTable =
  (id: string, formData: AddUpdateTablePayload): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(updateTableStart());

      const query = new Parse.Query(TableClassName);
      const table = await query.get(id);

      table.set(formData);
      await table.save();
      dispatch(updateTableSuccess(formatTable(table)));
    } catch (error) {
      dispatch(updateTableFailed(error.toString()));
      Sentry.captureException(error);
      // eslint-disable-next-line no-console
      console.error('updateTable error', error);
    }
  };
export const updateTableAvailability =
  (id: string, available: boolean): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(updateTableAvailabilityStart());

      const query = new Parse.Query(TableClassName);
      const table = await query.get(id);

      table.set('available', available);
      await table.save();
      dispatch(
        updateTableAvailabilitySuccess({table: formatTable(table), available}),
      );
    } catch (error) {
      dispatch(updateTableAvailabilityFailed(error.toString()));
      Sentry.captureException(error);
      // eslint-disable-next-line no-console
      console.error('updateTable error', error);
    }
  };
export const deleteTable =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(deleteTableStart());

      const query = new Parse.Query(TableClassName);
      const table = await query.get(id);

      await table.destroy();
      dispatch(deleteTableSuccess(id));
    } catch (error) {
      dispatch(deleteTableFailed(error.toString()));
      Sentry.captureException(error);
      // eslint-disable-next-line no-console
      console.error('deleteTable error', error);
    }
  };
